import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function BitcoinTechPage87() {
  const contentRef = useRef(null);
  const titleText = "Forks in Bitcoin – Soft Forks vs Hard Forks ";
  const links = {
    prev: "/bitcoin-tech-page-86",
    index: "/index-bitcoin-tech",
    next: "/bitcoin-tech-page-88",
  };
  const questions = [
    {
      text: "1. Which of the following best describes a soft fork in Bitcoin?",
      options: [
        {
          value: "A",
          label:
            "A. A backward-incompatible rule change that requires all nodes to upgrade",
        },
        {
          value: "B",
          label:
            "B. A backward-compatible rule change that tightens consensus rules",
        },
        {
          value: "C",
          label:
            "C. A new blockchain created from the old one due to disagreement",
        },
        {
          value: "D",
          label: "D. A temporary split caused by miner misbehavior",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What signaling mechanism is used by Bitcoin miners to indicate readiness for a soft fork?",
      options: [
        {
          value: "A",
          label: "A. Through the version bits field in the block header",
        },
        {
          value: "B",
          label: "B. By broadcasting a special transaction",
        },
        {
          value: "C",
          label: "C. By adjusting the difficulty target",
        },
        {
          value: "D",
          label: "D. Through a vote in the mempool",
        },
      ],
      correct: "A",
    },
    {
      text: "3. Which of the following is an example of a Bitcoin hard fork?",
      options: [
        {
          value: "A",
          label: "A. Taproot",
        },
        {
          value: "B",
          label: "B. SegWit",
        },
        {
          value: "C",
          label: "C. Bitcoin Cash",
        },
        {
          value: "D",
          label: "D. BIP 9",
        },
      ],
      correct: "C",
    },
    {
      text: "4. Why might a future quantum computing breakthrough lead to a soft fork in Bitcoin?",
      options: [
        { value: "A", label: "A. To change the block size limit" },
        {
          value: "B",
          label: "B. To introduce a new mining algorithm",
        },
        {
          value: "C",
          label:
            "C. To upgrade signature schemes to quantum-resistant cryptography",
        },
        {
          value: "D",
          label: "D. To increase transaction throughput",
        },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={87}
        timerMinutes={60}
        currentLevel={3}
        unlockedBadges={11}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 87</b>! Today’s lesson explores one of the most
        important mechanisms through which Bitcoin evolves: forks. You will
        learn how the concept of a fork originates in computer science and how
        it applies uniquely to Bitcoin’s consensus-driven architecture. By
        examining soft forks and hard forks through mathematical definitions,
        real-world examples like SegWit, Taproot, Bitcoin Cash, and Bitcoin
        Gold, and even future possibilities such as quantum-resistant upgrades,
        this lesson reveals how rule changes shape Bitcoin’s past, present, and
        future while preserving its decentralization.
        <br />
        <br />
        <b>The Concept of Forks </b>
        <br />
        <br />
        In computer science, a fork is a divergence in the state or evolution of
        a system. The term originally referred to the creation of a new process
        by duplicating an existing one, as in the Unix fork() system call. Over
        time, it took on a broader meaning in the world of software development,
        describing situations where two versions of the same codebase evolve
        independently.
        <br />
        When developers copy the source code of a project and begin modifying it
        separately, they create a software fork. The original and the new
        version may later become incompatible due to different design decisions,
        new features, or protocol rules. <br />
        In distributed systems such as blockchains, a fork refers to a situation
        where two or more nodes disagree on the next valid state of the ledger.
        Since blockchain consensus depends on all honest participants agreeing
        on the same sequence of blocks, a fork can occur temporarily or
        permanently.
        <br />
        To understand the deeper distinction between soft forks and hard forks,
        it helps to visualize the blockchain as a series of valid states
        governed by a set of protocol rules R. Each block Bi​ transitions the
        system from state Si ​ to Si+1 ​ under the constraint that Bi​ satisfies
        all rules in R.
        <br />
        <br />
        If new rules R′ are introduced, then:
      </p>
      <div className="code-block">
        If R′⊆ R, meaning the new rule set is more restrictive, nodes following
        R′ will still accept all blocks valid under R′, but older nodes may also
        accept them. This is a soft fork.
        <br />
        <br />
        If R′ ⊃ R, meaning the new rule set expands what is valid, then nodes
        following R will reject some blocks valid under R′. This is a hard fork.
      </div>
      <p className="content">Mathematically, we can express it as:</p>
      <p className="code-block">
        Soft fork: V(R′) ⊆ V(R) <br />
        Hard fork: V(R′) ⊃ V(R)
      </p>
      <p className="content">
        where V(R) is the set of valid blocks under the rule set R .<br />
        <br />
        <b>Forks in the Context of Bitcoin</b>
        <br />
        <br />
        Bitcoin’s consensus rules define what constitutes a valid block and
        valid transactions. All nodes must agree on these rules to maintain a
        single canonical chain. If some nodes begin enforcing different rules
        than others, the network can split into two incompatible versions, each
        following its own rule set.
        <br />
        <br />
        In Bitcoin, forks occur at two levels:
        <ul>
          <li>
            Temporary forks (chain splits): When two miners find a block nearly
            simultaneously, leading to two competing tips. These are resolved
            automatically when the next block is found.
          </li>
          <li>
            Protocol forks (rule changes): When the consensus rules change,
            requiring coordination among participants.
          </li>
        </ul>
        Bitcoin developers classify consensus rule changes as soft forks or hard
        forks, depending on how they affect backward compatibility.
        <br />
        <br />
        <b>Understanding Soft Forks</b>
        <br />
        <br />A soft fork in Bitcoin is a backward-compatible upgrade to the
        consensus rules that tightens the conditions for what is considered a
        valid block or transaction.
        <br />
        Old nodes that have not upgraded will continue to accept new blocks
        produced under the stricter rules as long as the upgraded majority
        enforces them. However, old nodes will not fully understand the new
        rules; they merely see them as valid but possibly unrecognized
        transactions.
        <br />
        <br />
        <b>Example: Segregated Witness (SegWit)</b>
        <br />
        <br />
        SegWit, activated in August 2017, is a prime example of a Bitcoin soft
        fork. Its primary goal was to fix transaction malleability and increase
        effective block capacity.
        <br />
        Before SegWit, the transaction ID (txid) was computed as a hash of the
        serialized transaction data, which included the scriptSig (signature).
        Since signatures could be modified without changing the meaning of the
        transaction, txids could be altered, creating malleability issues.{" "}
        <br />
        SegWit introduced a new structure where signatures are moved outside the
        base transaction data into a separate witness structure. This change
        required a rule modification, but instead of invalidating old nodes, the
        developers used a clever soft fork mechanism.
        <br />
        Under SegWit:
        <ul>
          <li>
            Non-upgraded nodes still see SegWit transactions as standard
            anyone-can-spend outputs.
          </li>
          <li>
            Upgraded nodes enforce additional rules about the witness data.
          </li>
        </ul>
        To implement SegWit, miners signaled readiness via version bits in the
        block header. The block header’s <i>nVersion </i>field contains bits
        that can be used to signal support for specific soft forks.
        <br />
        The signaling mechanism was introduced through BIP 9 (VersionBits). Each
        soft fork proposal is assigned a bit, and miners indicate support by
        setting that bit to 1.
        <br />
        Activation parameters include:
        <ul>
          <li>Start time and timeout (Unix timestamps)</li>
          <li>Threshold (95% of blocks in a 2016-block period)</li>
        </ul>
        Mathematically, the activation can be seen as:
      </p>
      <p className="code-block">
        P signal ​= N signal/2016 ​ ​ <br />
        If Psignal ≥ 0.95 then soft fork locks in.
      </p>
      <p className="content">
        <b>Example: Taproot</b>
        <br />
        <br />
        Taproot, activated in November 2021, is another major soft fork in
        Bitcoin. It combined Schnorr signatures and MAST (Merkelized Abstract
        Syntax Trees).
        <br />
        Under Taproot:
        <ul>
          <li>
            The new script type (Pay-to-Taproot) commits to either a public key
            or a script tree.
          </li>
          <li>
            Schnorr signatures allow key aggregation, enabling multiple signers
            to appear as one.
          </li>
          <li>
            Old nodes see Taproot outputs as valid Pay-to-PubkeyHash outputs
            without enforcing the new rules.
          </li>
        </ul>
        Like SegWit, Taproot used Speedy Trial, a modified version of BIP 9, for
        miner signaling.
        <br />
        <br />
        <b>Understanding Hard Forks</b>
        <br />
        <br />A hard fork is a non-backward-compatible change to Bitcoin’s
        consensus rules that loosens or expands the set of valid transactions or
        blocks.
        <br />
        Nodes that do not upgrade to the new rule set will reject blocks that
        upgraded nodes accept, leading to a permanent network split if there is
        not unanimous adoption.
        <br />
        <br />
        <b>Example: Bitcoin Cash (BCH)</b>
        <br />
        <br />
        Bitcoin Cash resulted from a hard fork on August 1, 2017, led by
        developers and miners who wanted to increase the block size limit from 1
        MB to 8 MB to allow more transactions per block.
        <br />
        This change made blocks valid under BCH rules that were invalid under
        the original Bitcoin rules. As a result, Bitcoin nodes rejected those
        blocks, creating two separate chains:
        <ul>
          <li>Bitcoin (BTC), following the original 1 MB limit</li>
          <li>Bitcoin Cash (BCH), following the expanded limit</li>
        </ul>{" "}
        The block header structure and Proof-of-Work algorithm remained the same
        (SHA-256), allowing miners to switch between the two chains. However,
        BCH introduced a replay protection mechanism to prevent transactions
        from being valid on both chains.
        <br />
        <br />
        <b>Example: Bitcoin Gold (BTG)</b>
        <br />
        <br />
        Bitcoin Gold forked from Bitcoin in October 2017. Its primary goal was
        to “make Bitcoin decentralized again” by changing the mining algorithm
        from SHA-256 to Equihash, which is more GPU-friendly and less dominated
        by ASICs.
        <br />
        This change fundamentally altered the consensus rules, requiring all
        nodes and miners to upgrade. It resulted in a completely separate
        network, incompatible with Bitcoin.
        <br />
        Mathematically, the Bitcoin Gold fork changed the Proof-of-Work
        function:
      </p>
      <p className="code-block">
        Po WBTC ​= SHA256(SHA256(block_header))
        <br />
        Po WBTG ​= Equihash(n, k)
      </p>
      <p className="content">
        Since Po WBTG ≠ Po WBTC​ , nodes enforcing Po WBTC​ would reject all BTG
        blocks as invalid.
      </p>
      <p className="content">
        <b>Implications of Forks</b>
        <br />
        <br />
        Soft forks demonstrate Bitcoin’s ability to evolve without breaking
        consensus among non-upgraded nodes. They rely on majority enforcement
        rather than unanimous agreement. <br />
        Hard forks, by contrast, require universal coordination. If a
        significant portion of the network refuses to upgrade, a permanent chain
        split occurs. <br />
        When a hard fork occurs, users holding private keys before the fork own
        coins on both chains. This creates “free coins,” as seen in Bitcoin Cash
        and Bitcoin Gold. However, it also introduces confusion, replay risks,
        and dilution of the brand. <br />
        Soft forks, on the other hand, maintain a single chain and therefore
        preserve economic unity.
        <br />
        <br />
        <b>Future Potential Soft Forks</b>
        <br />
        <br />
        <b>Example: OP_CHECKTEMPLATEVERIFY (CTV)</b>
        <br />
        <br />
        A future soft fork proposal, BIP 119 (OP_CHECKTEMPLATEVERIFY),
        introduces a new opcode that allows transaction outputs to commit to a
        predefined spending pattern.
        <br />
        CTV enables covenants, which can enforce rules on how coins are spent in
        the future. This has applications in congestion control, vaults, and
        payment batching.
        <br />
        The activation mechanism proposed for CTV would likely follow a BIP
        8-style activation, giving node operators more control over whether to
        enforce the fork.
        <br />
        Mathematically, a CTV commitment can be represented as:
      </p>
      <p className="code-block">H CTV​= SHA256(template)</p>
      <p className="content">
        The script checks whether the spending transaction matches the
        pre-committed template, thus tightening validation rules, making it a
        soft fork.
        <br />
        <br />
        <b>Quantum Computing and the Future of Forks</b>
        <br />
        <br />
        Quantum computers pose a potential threat to Bitcoin’s cryptographic
        foundations, particularly the Elliptic Curve Digital Signature Algorithm
        (ECDSA), which relies on the difficulty of the Discrete Logarithm
        Problem (DLP) over secp256k1.
        <br />
        ECDSA security depends on the infeasibility of finding the private key k
        given the public key Q=kG, where G is the generator point on the
        elliptic curve.
        <br />
        If a quantum computer can efficiently solve the DLP using Shor’s
        algorithm, it can derive private keys from public keys, breaking
        Bitcoin’s security model.
        <br />
        <br />
        <b>Soft Fork to Quantum-Resistant Signatures</b>
        <br />
        <br />
        To mitigate such a threat, Bitcoin would need to migrate to
        quantum-resistant cryptographic primitives such as lattice-based or
        hash-based signatures (e.g., XMSS, SPHINCS+).
        <br />
        Since new signature schemes would restrict which types of signatures are
        valid, such a transition could be implemented as a soft fork.
        <br />
        For instance, new rules could require that all future outputs use
        quantum-safe scripts, while still allowing old nodes to see these
        outputs as standard.
        <br />
        Nodes enforcing the new rules would verify against the new algorithm,
        while older nodes would continue to accept them as valid under the prior
        format.
        <br />
        <br />
        <b>Summary Table</b>
        <br />
        <br />
      </p>
      <table className="trade-table ">
        <thead>
          <tr>
            <th>Type</th>
            <th>Compatibilty</th>
            <th>Example</th>
            <th>Effect</th>
            <th>Signal</th>
          </tr>
          <tr>
            <td>Soft Fork</td>
            <td>Backward compatible</td>
            <td>SegWit, Taproot, CTV</td>
            <td>Narrows valid block set</td>
            <td>Version bits (BIP 9, BIP 8)</td>
          </tr>
          <tr>
            <td>Hard Fork</td>
            <td>Not backward compatible</td>
            <td>Bitcoin Cash, Bitcoin Gold</td>
            <td>Expands valid block set</td>
            <td>Requires full node upgrade</td>
          </tr>
        </thead>
      </table>

      <p className="content">
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Forks represent the evolutionary mechanisms through which Bitcoin
        adapts, innovates, and hardens over time. Soft forks exemplify careful,
        conservative upgrades that preserve the cohesion of the network while
        introducing new capabilities. Hard forks, by contrast, are radical
        departures that risk fragmentation and confusion and can give rise to
        new chains.
        <br />
        Bitcoin’s design, community, and governance mechanisms favor soft forks
        as the path of least disruption. The SegWit and Taproot activations
        demonstrated that even deeply technical changes can be deployed with
        social consensus.
        <br />
        Future developments, from covenants to quantum resistance, will likely
        continue this pattern, tightening the rules, enhancing security, and
        maintaining the shared chain of truth that underpins Bitcoin’s monetary
        integrity.
        <br />
        In essence, a soft fork is Bitcoin’s way of upgrading without forgetting
        its past, while a hard fork is a decision to start anew. The resilience
        of Bitcoin lies in its capacity to evolve cautiously, ensuring that
        every rule change preserves the fundamental principle that unites its
        users: consensus without coercion.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default BitcoinTechPage87;
